import React from 'react';
import { useParams, Navigate, Link, useSearchParams } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowRight } from 'lucide-react';
import { ProductCard } from '../components/ProductCard';
import { Product } from '../data/products';
import { useStore } from '../context/StoreContext';
import { sortProducts, SORT_OPTIONS, isValidSortKey, SortKey } from '../utils/sortProducts';
type CategoryDef = {
  title: string;
  desc: string;
  filter: (p: Product) => boolean;
};
const categoryMap: Record<string, CategoryDef> = {
  'complete-systems': {
    title: 'Complete Yarbo Systems',
    desc: 'Yarbo robotic systems that include the Yarbo Core and the modules needed for the job. Best starting point for new buyers.',
    filter: p => p.category === 'Complete Systems' || p.publicCategories?.includes('Complete Systems')
  },
  'lawn-mowers': {
    title: 'Yarbo Robot Lawn Mowers',
    desc: 'Wire-free, autonomous robotic mowing for properties of every size. Available as complete systems and individual modules.',
    filter: p => !!p.publicCategories?.includes('Robot Lawn Mowers')
  },
  'snow-blowers': {
    title: 'Yarbo Robotic Snow Blowers',
    desc: 'True 2-stage autonomous snow clearing for driveways, walkways, and large properties.',
    filter: p => !!p.publicCategories?.includes('Robotic Snow Blowers')
  },
  'leaf-blowers': {
    title: 'Yarbo Leaf Blowers',
    desc: 'High-force robotic leaf and debris clearing for wooded lots and large properties.',
    filter: p => !!p.publicCategories?.includes('Leaf Blowers')
  },
  bundles: {
    title: 'Yarbo Bundles',
    desc: 'Multi-module systems shipped with the Yarbo Core for year-round yard automation.',
    filter: p => p.primaryGroup === 'Bundle' || p.category === 'Bundles' || !!p.publicCategories?.includes('Bundles')
  },
  modules: {
    title: 'Yarbo Modules',
    desc: 'Expansion modules for customers who already own or are purchasing a compatible Yarbo Core.',
    filter: p => p.primaryGroup === 'Module' || p.category === 'Modules'
  },
  accessories: {
    title: 'Yarbo Accessories',
    desc: 'Blades, antennas, mounts, grease, covers, and replacement parts for Yarbo Y Series systems.',
    filter: p => p.primaryGroup === 'Accessory' || p.category === 'Accessories'
  },
  'modules-accessories': {
    title: 'Yarbo Modules & Accessories',
    desc: 'Modules, replacement parts, batteries, chargers, antennas, covers, blades, and other Yarbo Y Series accessories.',
    filter: p => p.primaryGroup === 'Module' || p.primaryGroup === 'Accessory' || p.category === 'Modules' || p.category === 'Accessories' || !!p.publicCategories?.includes('Modules & Accessories')
  },
  'lawn-care': {
    title: 'Yarbo Lawn Care Products',
    desc: 'Robot lawn mowers, mower bundles, mower modules, blades, tracks, and lawn-care replacement parts.',
    filter: p => !!p.taskCategories?.includes('Lawn Care') || p.season.includes('Lawn Care')
  },
  'snow-removal': {
    title: 'Yarbo Snow Removal Products',
    desc: 'Robotic snow blowers, snow bundles, snow modules, snow tracks, scraper bars, plow blades, and winter accessories.',
    filter: p => !!p.taskCategories?.includes('Snow Removal') || p.season.includes('Snow Removal')
  },
  'leaf-cleanup': {
    title: 'Yarbo Leaf Cleanup Products',
    desc: 'Yarbo blower systems, blower modules, blower covers, and bundles built for leaves and outdoor debris.',
    filter: p => !!p.taskCategories?.includes('Leaf Cleanup') || p.season.includes('Leaf Cleanup')
  },
  'trimming-edging': {
    title: 'Yarbo Trimming & Edging Products',
    desc: 'Yarbo Trimmer Module, Back Brace Mount, and trimmer line accessories for edge and detail trimming.',
    filter: p => !!p.taskCategories?.includes('Trimming & Edging')
  },
  'tow-transport': {
    title: 'Yarbo Tow & Transport Products',
    desc: 'Tow hitch and transport-related Yarbo Y Series accessories.',
    filter: p => !!p.taskCategories?.includes('Tow & Transport')
  }
};
export function Category() {
  const {
    slug
  } = useParams<{
    slug: string;
  }>();
  const {
    products
  } = useStore();
  const [searchParams, setSearchParams] = useSearchParams();
  const data = categoryMap[slug || ''];
  if (!data) return <Navigate to="/shop" replace data-id="element-1083" />;
  const sortParam = searchParams.get('sort');
  const sortBy: SortKey = isValidSortKey(sortParam) ? sortParam : 'price-desc';
  const updateSort = (next: SortKey) => {
    const params = new URLSearchParams(searchParams);
    params.set('sort', next);
    setSearchParams(params);
  };
  // Filter first, then sort.
  const categoryProducts = sortProducts(products.filter(data.filter), sortBy);
  return <div className="bg-gray-50 min-h-screen" data-id="element-1084">
      <div className="bg-white border-b border-gray-200" data-id="element-1085">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10" data-id="element-1086">
          <motion.h1 initial={{
          opacity: 0,
          y: -10
        }} animate={{
          opacity: 1,
          y: 0
        }} className="text-3xl md:text-4xl font-extrabold text-yarbo-dark mb-2" data-id="element-1087">
            {data.title}
          </motion.h1>
          <p className="text-gray-700 max-w-3xl" data-id="element-1088">{data.desc}</p>
          <div className="mt-4 text-sm text-gray-500 font-medium" data-id="element-1089">
            {categoryProducts.length} product
            {categoryProducts.length !== 1 ? 's' : ''}
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10" data-id="element-1090">
        {categoryProducts.length > 0 && <div className="flex justify-end mb-5" data-id="element-1091">
            <select value={sortBy} onChange={e => updateSort(e.target.value as SortKey)} className="text-sm font-semibold border border-gray-200 rounded-lg px-3 py-2 bg-white focus:ring-2 focus:ring-yarbo-yellow focus:border-transparent outline-none" data-id="element-1092">
              {SORT_OPTIONS.map(opt => <option key={opt.value} value={opt.value} data-id="element-1093">
                  {opt.label}
                </option>)}
            </select>
          </div>}

        {categoryProducts.length > 0 ? <div className="grid grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4 sm:gap-6" data-id="element-1094">
            {categoryProducts.map((p, idx) => <motion.div key={p.id} initial={{
          opacity: 0,
          y: 10
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 0.3,
          delay: Math.min(idx * 0.04, 0.4)
        }} data-id="element-1095">
                <ProductCard product={p} compact={categoryProducts.length > 8} data-id="element-1096" />
              </motion.div>)}
          </div> : <div className="text-center py-16 bg-white rounded-2xl border border-gray-200" data-id="element-1097">
            <p className="text-gray-600 mb-4" data-id="element-1098">
              No products in this category yet.
            </p>
            <Link to="/shop" className="inline-flex items-center font-bold text-yarbo-dark hover:text-black" data-id="element-1099">
              Browse all Yarbo products <ArrowRight className="ml-2 w-4 h-4" data-id="element-1100" />
            </Link>
          </div>}
      </div>
    </div>;
}